import { AgreeToTermsPolicy, SignInIdentifier } from '@logto/schemas';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import LockIcon from '@/assets/icons/lock.svg?react';
import { PasswordInputField, SmartInputField } from '@/components/InputFields';
import CaptchaBox from '@/containers/CaptchaBox';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import { useIsAimsLayout } from '@/hooks/use-layout-mode';
import usePasswordPolicyChecker from '@/hooks/use-password-policy-checker';
import usePrefilledIdentifier from '@/hooks/use-prefilled-identifier';
import useSingleSignOnWatch from '@/hooks/use-single-sign-on-watch';
import useTerms from '@/hooks/use-terms';
import Button from '@/shared/components/Button';
import ErrorMessage from '@/shared/components/ErrorMessage';
import type { IdentifierInputValue } from '@/shared/components/InputFields/SmartInputField';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';
import { setPendingPassword } from '@/utils/pending-password';

import styles from './index.module.scss';
import useOnSubmit from './use-on-submit';

type Props = {
  readonly className?: string;
  // eslint-disable-next-line react/boolean-prop-naming
  readonly autoFocus?: boolean;
  readonly signUpMethods: SignInIdentifier[];
};

type FormState = {
  identifier: IdentifierInputValue;
  password: string;
};

const IdentifierRegisterForm = ({ className, autoFocus, signUpMethods }: Props) => {
  const { t } = useTranslation();
  const { termsValidation, agreeToTermsPolicy } = useTerms();

  const { errorMessage, clearErrorMessage, onSubmit } = useOnSubmit();

  const { setIdentifierInputValue } = useContext(UserInteractionContext);
  const { experienceSettings } = useContext(PageContext);
  // Applicants (AIMS layout): pick Phone or Email, and type the password here, before the code.
  const isApplicant =
    useIsAimsLayout() && experienceSettings?.branding.aimsAudience === 'applicant';
  const channels = [SignInIdentifier.Phone, SignInIdentifier.Email].filter((method) =>
    signUpMethods.includes(method)
  );
  const prefilledIdentifier = usePrefilledIdentifier({ enabledIdentifiers: signUpMethods });
  // Start on the channel of a prefilled email or phone (login hint, or back from the code page).
  const [channel, setChannel] = useState(
    channels.find((method) => method === prefilledIdentifier.type) ?? channels[0]
  );
  const enabledTypes = isApplicant && channels.length > 1 && channel ? [channel] : signUpMethods;
  const asksPassword = isApplicant && Boolean(experienceSettings.signUp.password);
  const [passwordError, setPasswordError] = useState<string>();
  const checkPassword = usePasswordPolicyChecker({ setErrorMessage: setPasswordError });

  const {
    watch,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    control,
    resetField,
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    defaultValues: { identifier: prefilledIdentifier },
  });

  // Watch identifier field and check single sign on method availability
  const { showSingleSignOnForm, navigateToSingleSignOn } = useSingleSignOnWatch(
    watch('identifier')
  );

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      clearErrorMessage();

      void handleSubmit(async ({ identifier: { type, value }, password }) => {
        if (!type) {
          return;
        }

        if (asksPassword && !(await checkPassword(password))) {
          return;
        }

        setIdentifierInputValue({ type, value });

        if (
          agreeToTermsPolicy &&
          agreeToTermsPolicy !== AgreeToTermsPolicy.Automatic &&
          !(await termsValidation())
        ) {
          return;
        }

        if (showSingleSignOnForm) {
          await navigateToSingleSignOn();
          return;
        }

        if (asksPassword) {
          setPendingPassword(password, { type, value });
        }

        await onSubmit(type, value);
      })(event);
    },
    [
      agreeToTermsPolicy,
      asksPassword,
      checkPassword,
      clearErrorMessage,
      handleSubmit,
      navigateToSingleSignOn,
      onSubmit,
      setIdentifierInputValue,
      showSingleSignOnForm,
      termsValidation,
    ]
  );

  return (
    <form className={classNames(styles.form, className)} onSubmit={onSubmitHandler}>
      {isApplicant && channels.length > 1 && (
        <div
          className={styles.channels}
          role="group"
          aria-label={t('description.aims_layout.sign_up_with')}
        >
          {channels.map((method) => (
            <button
              key={method}
              type="button"
              aria-pressed={method === channel}
              className={classNames(styles.channel, method === channel && styles.active)}
              onClick={() => {
                // A phone number typed under Email (or the other way) would submit as the wrong type.
                resetField('identifier', { defaultValue: { value: '' } });
                setChannel(method);
              }}
            >
              {t(method === SignInIdentifier.Phone ? 'input.phone_number' : 'input.email')}
            </button>
          ))}
        </div>
      )}
      <Controller
        control={control}
        name="identifier"
        rules={{
          validate: ({ type, value }) => {
            if (!type || !value) {
              return getGeneralIdentifierErrorMessage(signUpMethods, 'required');
            }

            const errorMessage = validateIdentifierField(
              type,
              value,
              experienceSettings?.usernamePolicy
            );

            if (errorMessage) {
              return typeof errorMessage === 'string'
                ? t(`error.${errorMessage}`)
                : t(`error.${errorMessage.code}`, errorMessage.data ?? {});
            }

            return true;
          },
        }}
        render={({ field, formState: { defaultValues } }) => (
          <SmartInputField
            key={channel}
            autoComplete="off"
            autoFocus={autoFocus}
            className={styles.inputField}
            {...field}
            defaultValue={defaultValues?.identifier?.value}
            isDanger={!!errors.identifier || !!errorMessage}
            errorMessage={errors.identifier?.message}
            enabledTypes={enabledTypes}
          />
        )}
      />
      {asksPassword && (
        <Controller
          control={control}
          name="password"
          rules={{ required: t('error.general_required', { types: [t('input.password')] }) }}
          render={({ field }) => (
            <PasswordInputField
              className={styles.inputField}
              autoComplete="new-password"
              label={t('input.password')}
              {...field}
              isDanger={!!errors.password || !!passwordError}
              errorMessage={errors.password?.message ?? passwordError}
              onChange={(event) => {
                setPasswordError(undefined);
                field.onChange(event);
              }}
            />
          )}
        />
      )}
      {errorMessage && <ErrorMessage className={styles.formErrors}>{errorMessage}</ErrorMessage>}
      {showSingleSignOnForm && (
        <div className={styles.message}>{t('description.single_sign_on_enabled')}</div>
      )}
      {/**
       * Have to use css to hide the terms element.
       * Remove element from dom will trigger a form re-render.
       * Form rerender will trigger autofill.
       * If the autofill value is SSO enabled, it will always show SSO form.
       */}
      <TermsAndPrivacyCheckbox
        className={classNames(
          styles.terms,
          /**
           * Hide the terms checkbox when the policy is set to `Automatic`.
           * In registration, the terms checkbox is always shown for `Manual` and `ManualRegistrationOnly` policies.
           */
          agreeToTermsPolicy === AgreeToTermsPolicy.Automatic && styles.hidden
        )}
      />
      <CaptchaBox />
      <Button
        name="submit"
        title={showSingleSignOnForm ? 'action.single_sign_on' : 'action.create_account'}
        icon={showSingleSignOnForm ? <LockIcon /> : undefined}
        htmlType="submit"
        isLoading={isSubmitting}
      />
      <input hidden type="submit" />
    </form>
  );
};

export default IdentifierRegisterForm;
